import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Preserves sub-path and query string when redirecting legacy /footypicks/* URLs to /public/*
function LegacySlugRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/footypicks/, '/public');
  return <Navigate to={`${newPath}${location.search}${location.hash}`} replace />;
}
import { AuthProvider, ThemeProvider, ToastProvider, PredictionsProvider, CompetitionsProvider, GamesProvider, ForcePasswordChangePage, RequirePasswordChangeGuard } from '@footypicks/shared';
import { restoreDefaultBranding } from '@footypicks/shared/utils/favicon';
import { HomePage } from './pages/HomePage';
import { OrganizationLandingPage } from './pages/OrganizationLandingPage';

// Lazy-loaded routes — each sub-app loads only when the user navigates to it.
// This dramatically reduces initial bundle size for visitors landing on the
// homepage or an organization landing page.
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AdminEmailsPage = lazy(() => import('./pages/AdminEmailsPage').then(m => ({ default: m.AdminEmailsPage })));
const CompetitionUpdatesOptOutPage = lazy(() =>
  import('@footypicks/shared').then((m) => ({ default: m.CompetitionUpdatesOptOutPage }))
);
const OrganizationUpdatesOptOutPage = lazy(() =>
  import('@footypicks/shared').then((m) => ({ default: m.OrganizationUpdatesOptOutPage }))
);

const LMSAppContent = lazy(() => import('@footypicks/lms/App').then(m => ({ default: m.LMSAppContent })));
const LMSSuccessPage = lazy(() => import('@footypicks/lms/App').then(m => ({ default: m.SuccessPage })));
const LMSCancelPage = lazy(() => import('@footypicks/lms/App').then(m => ({ default: m.CancelPage })));
const LMSAdminPage = lazy(() => import('@footypicks/lms/App').then(m => ({ default: m.CompetitionManagementPage })));
const ResetPasswordPage = lazy(() => import('@footypicks/lms/App').then(m => ({ default: m.ResetPasswordPage })));
const ClubKeeperHandoffPage = lazy(() =>
  import('./pages/ClubKeeperHandoffPage').then((m) => ({ default: m.ClubKeeperHandoffPage }))
);

const PredictorAppContent = lazy(() => import('@footypicks/predictor/App').then(m => ({ default: m.PredictorAppContent })));
const PredictorSuccessPage = lazy(() => import('@footypicks/predictor/App').then(m => ({ default: m.PredictorSuccessPage })));
const PredictorCancelPage = lazy(() => import('@footypicks/predictor/App').then(m => ({ default: m.PredictorCancelPage })));
const PredictorAdminPage = lazy(() => import('@footypicks/predictor/App').then(m => ({ default: m.CompetitionManagementPage })));

const WorldCupAppContent = lazy(() => import('@footypicks/worldcup/App').then(m => ({ default: m.WorldCupAppContent })));
const PublicWorldCupRedirect = lazy(() => import('./pages/PublicWorldCupRedirect').then(m => ({ default: m.PublicWorldCupRedirect })));
const WorldCupHoldingPage = lazy(() => import('./pages/WorldCupHoldingPage').then(m => ({ default: m.WorldCupHoldingPage })));
import { WC_HOLDING_PAGE_ENABLED } from './config/worldCupHoldingPage';

const WorldCupRoute = ({ live }: { live: JSX.Element }) =>
  WC_HOLDING_PAGE_ENABLED ? <WorldCupHoldingPage /> : live;

// Neutral fallback that matches the global background defined in index.html.
// Avoids a white flash while a route chunk is fetched on first navigation.
const RouteFallback = () => (
  <div className="min-h-screen bg-slate-100 dark:bg-gray-900" aria-hidden="true" />
);

function App() {
  useEffect(() => {
    // Set up default branding for main app
    restoreDefaultBranding();
  }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <CompetitionsProvider>
          <GamesProvider>
            <PredictionsProvider>
              <ToastProvider>
                <Router>
                  <RequirePasswordChangeGuard>
                    <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        {/* Main landing page - footypicks.co.uk */}
                        <Route path="/" element={<HomePage />} />

                        {/* Legacy slug redirects: /footypicks/* → /public/* (preserves sub-paths and query strings)
                            NOTE: We must declare explicit static routes for each sub-path because React Router v6
                            ranks `/:organizationSlug/lms` higher than `/footypicks/*` (concrete static beats splat). */}
                        <Route path="/footypicks" element={<Navigate to="/public" replace />} />
                        <Route path="/footypicks/admin" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/lms" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/lms/success" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/lms/cancel" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/lms/admin" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/predictor" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/predictor/success" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/predictor/cancel" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/predictor/admin" element={<LegacySlugRedirect />} />
                        <Route path="/footypicks/worldcup" element={<WorldCupRoute live={<PublicWorldCupRedirect />} />} />
                        {/* Catch-all for any other /footypicks/* path (lower priority than the above) */}
                        <Route path="/footypicks/*" element={<LegacySlugRedirect />} />

                        {/* Organization landing page - shows available competitions */}
                        <Route path="/:organizationSlug" element={<OrganizationLandingPage />} />

                        {/* Organization Admin Portal */}
                        <Route path="/:organizationSlug/admin/emails" element={<AdminEmailsPage />} />
                        <Route path="/:organizationSlug/admin" element={<AdminPage />} />

                        {/* LMS Competition Routes */}
                        <Route path="/:organizationSlug/lms" element={<LMSAppContent />} />
                        <Route path="/:organizationSlug/lms/success" element={<LMSSuccessPage />} />
                        <Route path="/:organizationSlug/lms/cancel" element={<LMSCancelPage />} />
                        <Route path="/:organizationSlug/lms/admin" element={<LMSAdminPage />} />

                        {/* Predictor Competition Routes */}
                        <Route path="/:organizationSlug/predictor" element={<PredictorAppContent />} />
                        <Route path="/:organizationSlug/predictor/success" element={<PredictorSuccessPage />} />
                        <Route path="/:organizationSlug/predictor/cancel" element={<PredictorCancelPage />} />
                        <Route path="/:organizationSlug/predictor/admin" element={<PredictorAdminPage />} />

                        {/* World Cup Predictor Routes */}
                        {/* /worldcup and /footypicks/worldcup resolve featured comp then redirect to /:org/worldcup?comp= */}
                        {/* Do NOT add a static /public/worldcup route — it would capture /public/worldcup?comp= and loop PublicWorldCupRedirect */}
                        <Route path="/worldcup" element={<WorldCupRoute live={<PublicWorldCupRedirect />} />} />
                        <Route path="/:organizationSlug/worldcup" element={<WorldCupRoute live={<WorldCupAppContent />} />} />

                        {/* Utility Routes */}
                        <Route path="/email-opt-out/competition-updates" element={<CompetitionUpdatesOptOutPage />} />
                        <Route path="/email-opt-out/organisation-updates" element={<OrganizationUpdatesOptOutPage />} />
                        <Route path="/auth/clubkeeper-handoff" element={<ClubKeeperHandoffPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
                      </Routes>
                    </Suspense>
                  </RequirePasswordChangeGuard>
                </Router>
              </ToastProvider>
            </PredictionsProvider>
          </GamesProvider>
        </CompetitionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
